/**
 * MapLibre's own stylesheet, reduced to the rules this globe actually needs,
 * injected once at runtime.
 *
 * Importing `maplibre-gl/dist/maplibre-gl.css` would force every consumer of
 * this source-first package through a bundler that handles CSS imports, and
 * would ship ~10KB of control/popup/marker chrome none of which exists here:
 * the globe adds no controls, no popups and no DOM markers — pins are GPU
 * circle layers. What is left is the canvas positioning and, critically, the
 * `touch-action` rules. Without those, a pinch-zoom on the globe scrolls the
 * page instead, which is an instant fail on the phone gate.
 *
 * The container also gets a background: the WebGL canvas is transparent
 * outside the sphere, so without one the page shows through the sky. Override
 * it by setting `--geogame-space` on (or above) the globe element.
 */

const STYLE_ELEMENT_ID = 'geogame-globe-runtime-css';

const CSS = `
.maplibregl-map{position:relative;overflow:hidden;-webkit-tap-highlight-color:rgba(0,0,0,0)}
.maplibregl-canvas{position:absolute;left:0;top:0}
.maplibregl-canvas-container.maplibregl-interactive{cursor:crosshair;-webkit-user-select:none;user-select:none}
.maplibregl-canvas-container.maplibregl-interactive:active{cursor:grabbing}
.maplibregl-canvas-container.maplibregl-touch-zoom-rotate,
.maplibregl-canvas-container.maplibregl-touch-zoom-rotate .maplibregl-canvas{touch-action:pan-x pan-y}
.maplibregl-canvas-container.maplibregl-touch-drag-pan,
.maplibregl-canvas-container.maplibregl-touch-drag-pan .maplibregl-canvas{touch-action:pinch-zoom}
.maplibregl-canvas-container.maplibregl-touch-zoom-rotate.maplibregl-touch-drag-pan,
.maplibregl-canvas-container.maplibregl-touch-zoom-rotate.maplibregl-touch-drag-pan .maplibregl-canvas{touch-action:none}
[data-geogame-globe]{width:100%;height:100%;position:relative;background:var(--geogame-space,#05070d)}
`;

/** Idempotent — safe across every mount, remount and hot reload. */
export function ensureRuntimeStyles(doc: Document | undefined): void {
  if (!doc || doc.getElementById(STYLE_ELEMENT_ID)) return;
  const el = doc.createElement('style');
  el.id = STYLE_ELEMENT_ID;
  el.textContent = CSS;
  doc.head.appendChild(el);
}
